import { type StructuredContentDocument } from '@repo/contracts/multi-use/structured-content/types';
import { css, cx } from '@repo/pandacss/styled-system/css';
import { AccessibleHiddenText } from '@repo/ui-v2/AccessibleHiddenText';
import { type WPTableColumn, type WPTableRow } from '../../hydration/WPTable/types';
import { renderStructuredContent } from '../../rendererContext';
import type { RendererProps } from '../../types';

export type WPTableProps = StructuredContentDocument<{
  headers: WPTableColumn[];
  rows: WPTableRow[];
  tableBolding: '' | 'NONE' | 'FIRST_ROW' | 'FIRST_COLUMN' | 'FIRST_ROW_COLUMN';
  isStriped: boolean;
  rowHover: boolean;
  rendererProps?: RendererProps;
}>;

const getTableBoldStyling = (tableBolding: string, rowIndex: number, columnIndex: number) => {
  if (tableBolding === 'FIRST_ROW') return rowIndex === 0;
  if (tableBolding === 'FIRST_COLUMN') return columnIndex === 0;
  if (tableBolding === 'FIRST_ROW_COLUMN') return rowIndex === 0 || columnIndex === 0;
  return false;
};

const wrapperClass = css({
  borderTopColor: 'primary',
  borderTopStyle: 'solid',
  borderTopWidth: 'medium',
  borderRightColor: 'neutral.lighter',
  borderRightStyle: 'solid',
  borderRightWidth: 'thin',
  borderBottomColor: 'neutral.lighter',
  borderBottomStyle: 'solid',
  borderBottomWidth: 'thin',
  borderLeftColor: 'neutral.lighter',
  borderLeftStyle: 'solid',
  borderLeftWidth: 'thin',
  overflowX: 'auto',
});

const tableClass = css({
  width: 'full',
  borderCollapse: 'collapse',
});

const headerCellClass = css({
  paddingInlineStart: { base: '4', md: '2' },
  paddingInlineEnd: { base: '4', md: '2' },
  paddingBlockStart: { base: '2', md: '4' },
  paddingBlockEnd: { base: '2', md: '4' },
  minWidth: '[140px]',
  backgroundColor: 'neutral.lightest',
  borderBottomWidth: 'thin',
  borderBottomStyle: 'solid',
  borderBottomColor: 'neutral.lighter',
  // to match MUI we set the textAlign
  textAlign: 'left',
  verticalAlign: 'top',
  '&:first-of-type': {
    paddingLeft: '4',
  },
  '&:not(:first-of-type)': {
    borderLeftWidth: 'thin',
    borderLeftStyle: 'solid',
    borderLeftColor: 'neutral.lighter',
  },
});

const bodyCellClass = css({
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  paddingBlockStart: { base: '2', md: '4' },
  paddingBlockEnd: { base: '2', md: '4' },
  borderBottomWidth: 'thin',
  borderBottomStyle: 'solid',
  borderBottomColor: 'neutral.lighter',
  minWidth: '[140px]',
  '&:not(:first-of-type)': {
    borderLeftWidth: 'thin',
    borderLeftStyle: 'solid',
    borderLeftColor: 'neutral.lighter',
  },
  '& ul': {
    marginBlockStart: '4',
    marginBlockEnd: '4',
  },
});

const boldContentClass = css({ '& *': { fontWeight: 'bold' } });

const oddRowClass = css({ '&:nth-of-type(odd)': { backgroundColor: 'neutral.white' } });
const stripedEvenRowClass = css({ '&:nth-of-type(even)': { backgroundColor: 'neutral.lightest' } });
const unstripedEvenRowClass = css({ '&:nth-of-type(even)': { backgroundColor: 'neutral.white' } });
const rowHoverClass = css({ '&:hover td': { backgroundColor: 'secondary-light' } });

export const WPTable = ({ props }: WPTableProps) => {
  return (
    <div className={wrapperClass}>
      <table className={tableClass}>
        {props.headers && (
          <thead>
            <tr>
              {props?.headers?.map((header, i) => (
                <th
                  key={`sc-table-header-${i}`}
                  data-testid={`sc-table-header-${i}`}
                  colSpan={header.colspan}
                  className={cx(headerCellClass, !props.tableBolding && boldContentClass)}
                >
                  {header.content ? (
                    renderStructuredContent(header.content, props.rendererProps)
                  ) : (
                    <AccessibleHiddenText>Empty Table Header</AccessibleHiddenText>
                  )}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {props?.rows?.map((row: WPTableRow, rowIdx) => (
            <tr
              key={`sc-table-row-${rowIdx}`}
              className={cx(
                oddRowClass,
                props.isStriped ? stripedEvenRowClass : unstripedEvenRowClass,
                props.rowHover && rowHoverClass,
              )}
            >
              {row.columns.map((col: WPTableColumn, colIdx) => (
                <td
                  colSpan={col.colspan}
                  key={`sc-table-col-${col.id}`}
                  className={cx(
                    bodyCellClass,
                    getTableBoldStyling(props.tableBolding, rowIdx, colIdx) && boldContentClass,
                  )}
                >
                  {col.content && renderStructuredContent(col.content, props.rendererProps)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
